import { useMemo } from 'react';
import type { Candidate, PipelineStage } from '@/models/job';
import { SendInterviewConfirmationDialog } from '../interview/SendInterviewConfirmationDialog';
import { InterviewInvitation } from '../candidate/InterviewInvitation';
import type { CandidatePipelineMutations } from '@/hooks/useCandidatePipelineMutations';
import { createGTMTracking } from '@/utils/gtm/gtm-tracking';
import type { AcceptCandidatePayload } from '@/utils/bookingLink';
import { findStageByType, stageWillNotify } from '@/utils/pipeline-stages';
import { DeclineApplicationDialog } from './DeclineApplicationDialog';
import { HireCandidateDialog } from './HireCandidateDialog';

interface CandidatePipelineActionDialogsProps {
  candidate: Candidate;
  mutations: CandidatePipelineMutations;
  origin: string;
  /** Job pipeline stages — used for live automation copy. */
  stages?: PipelineStage[];
}

export function CandidatePipelineActionDialogs({
  candidate,
  mutations,
  origin,
  stages,
}: CandidatePipelineActionDialogsProps) {
  const active = mutations.activeCandidate ?? candidate;

  // Until pipeline loads, keep historical "will notify" copy (enabled default).
  const acceptedWillNotify = useMemo(() => {
    if (!stages) return true;
    return stageWillNotify(findStageByType(stages, 'accepted'));
  }, [stages]);
  const rejectedWillNotify = useMemo(() => {
    if (!stages) return true;
    return stageWillNotify(findStageByType(stages, 'rejected'));
  }, [stages]);
  const interviewingWillNotify = useMemo(() => {
    if (!stages) return true;
    return stageWillNotify(findStageByType(stages, 'interviewing'));
  }, [stages]);

  const acceptTracking = {
    close: createGTMTracking('APPROVE_CANDIDATE', {
      origin,
      target: 'cancelled',
    }),
    send: createGTMTracking('APPROVE_CANDIDATE', {
      origin,
      target: 'confirmed',
    }),
  };
  const invitation = {
    recipientEmail: active?.talentEmail,
    candidateName: active?.talentName,
    isSubmitting: mutations.isAccepting,
    willNotify: acceptedWillNotify,
    sendNotification: mutations.sendNotification,
    onSendNotificationChange: mutations.setSendNotification,
    onSend: (payload: AcceptCandidatePayload) => {
      void mutations.handleAccept(active, payload);
    },
    closeButtonTracking: acceptTracking.close,
    sendButtonTracking: acceptTracking.send,
  };

  return (
    <>
      <SendInterviewConfirmationDialog
        open={mutations.showSendInterviewDialog}
        candidateIds={mutations.activeCandidate ? [mutations.activeCandidate.id] : [candidate.id]}
        candidates={mutations.activeCandidate ? [mutations.activeCandidate] : [candidate]}
        onClose={mutations.closeSendInterviewDialog}
        onConfirm={mutations.confirmSendInterview}
        isPending={
          mutations.isSendingInterview ||
          mutations.isUpdatingEmail ||
          mutations.isSelectPending
        }
        origin={origin}
        missingEmailValue={mutations.pendingEmailForSend}
        onMissingEmailChange={mutations.setPendingEmailForSend}
        willNotify={interviewingWillNotify}
        sendNotification={mutations.sendNotification}
        onSendNotificationChange={mutations.setSendNotification}
      />

      <InterviewInvitation
        open={mutations.showAcceptDialog}
        onClose={mutations.closeAccept}
        {...invitation}
      />

      <DeclineApplicationDialog
        open={mutations.showRejectDialog}
        onClose={() => mutations.setShowRejectDialog(false)}
        onConfirm={() => {
          void mutations.confirmReject();
        }}
        candidateName={active?.talentName}
        candidateEmail={active?.talentEmail}
        isPending={mutations.isRejecting}
        origin={origin}
        willNotify={rejectedWillNotify}
        sendNotification={mutations.sendNotification}
        onSendNotificationChange={mutations.setSendNotification}
      />

      <HireCandidateDialog
        open={mutations.showHireDialog}
        onClose={() => mutations.setShowHireDialog(false)}
        onConfirm={() => {
          void mutations.confirmHire();
        }}
        candidateName={active?.talentName}
        candidateEmail={active?.talentEmail}
        isPending={mutations.isHiring}
        origin={origin}
      />
    </>
  );
}
